import type { Identifier } from "ra-core";

import type {
  AuditLogEntry,
  DocumentValidationResult,
  DocumentVersion,
  DocumentWithVersions,
  OperationDocumentSummary,
  UploadOperationDocumentInput,
} from "../../types";
import {
  buildOperationDocumentStoragePath,
  OPERATION_DOCUMENT_SIGNED_URL_EXPIRY_SECONDS,
  OPERATION_DOCUMENTS_BUCKET,
} from "../commons/operationDocuments";
import { computeSha256Hex } from "../commons/sha256";
import { getSupabaseClient } from "./supabase";

/**
 * Almikia Epic E5 (TASK-034): typed wrappers over the documents SQL of
 * TASK-033. The SECURITY DEFINER functions are the authority (role matrix,
 * validator-is-not-advisor rule, append-only versions) and raise Spanish
 * messages that are surfaced as-is; reads rely on RLS (a hidden operation has
 * no documents, `contabilidad` reads nothing). Downloads go through a
 * short-lived signed URL only.
 */
const fail = (name: string, error: { message?: string }): never => {
  console.error(`${name}.error`, error);
  throw new Error(error.message || "No se pudo completar la acción");
};

const callVersionRpc = async (
  name: string,
  args: Record<string, unknown>,
): Promise<DocumentVersion> => {
  const { data, error } = await getSupabaseClient().rpc(name, args);
  if (error) fail(name, error);
  return data as DocumentVersion;
};

export const createSupabaseDocumentsMethods = () => ({
  /** An operation's documents with all their versions (soft-deleted ones included, flagged). */
  async listOperationDocuments(
    operationId: string,
  ): Promise<DocumentWithVersions[]> {
    const { data, error } = await getSupabaseClient()
      .from("documents")
      .select("*, versions:document_versions(*)")
      .eq("operation_id", operationId)
      .order("id", { ascending: true })
      .order("version_number", {
        referencedTable: "versions",
        ascending: true,
      });
    if (error) fail("listOperationDocuments", error);
    return (data ?? []) as DocumentWithVersions[];
  },

  /** `operation_documents_summary`: one row per document with its latest live version. */
  async getOperationDocumentsSummary(
    operationId: string,
  ): Promise<OperationDocumentSummary[]> {
    const { data, error } = await getSupabaseClient()
      .from("operation_documents_summary")
      .select("*")
      .eq("operation_id", operationId)
      .order("document_id", { ascending: true });
    if (error) fail("getOperationDocumentsSummary", error);
    return (data ?? []) as OperationDocumentSummary[];
  },

  /**
   * Hashes the file (SHA-256), uploads it to the private `operation-documents`
   * bucket under `<operation_id>/<uuid>-<file_name>` and registers the version
   * with `register_document_version`. The upload never overwrites an object.
   */
  async uploadOperationDocument(
    input: UploadOperationDocumentInput,
  ): Promise<DocumentVersion> {
    const { operationId, file, docType, title, documentId } = input;
    const sha256 = await computeSha256Hex(file);
    const storagePath = buildOperationDocumentStoragePath(
      operationId,
      file.name,
    );
    const mimeType = file.type || "application/octet-stream";

    const { error: uploadError } = await getSupabaseClient()
      .storage.from(OPERATION_DOCUMENTS_BUCKET)
      .upload(storagePath, file, { upsert: false, contentType: mimeType });
    if (uploadError) fail("uploadOperationDocument.upload", uploadError);

    // ASSUMPTION: if registering fails, the uploaded object is orphaned (the
    // bucket has no delete policy by design); it is unreachable without a row.
    return callVersionRpc("register_document_version", {
      p_operation_id: operationId,
      p_doc_type: docType,
      p_storage_path: storagePath,
      p_sha256: sha256,
      p_file_name: file.name,
      p_mime_type: mimeType,
      p_size_bytes: file.size,
      p_document_id: documentId ?? null,
      p_title: title ?? null,
    });
  },

  /** `review_document_version()`: marks a version as reviewed (once). */
  reviewDocumentVersion: (
    versionId: Identifier,
    note?: string,
  ): Promise<DocumentVersion> =>
    callVersionRpc("review_document_version", {
      p_version_id: versionId,
      p_note: note ?? null,
    }),

  /** `validate_document_version()`: validado, or observado / rechazado with a mandatory reason. */
  validateDocumentVersion: (
    versionId: Identifier,
    result: DocumentValidationResult,
    reason?: string,
  ): Promise<DocumentVersion> =>
    callVersionRpc("validate_document_version", {
      p_version_id: versionId,
      p_result: result,
      p_reason: reason ?? null,
    }),

  /** `soft_delete_document()`: the reason is mandatory; the rows and files are kept. */
  async softDeleteDocument(
    documentId: Identifier,
    reason: string,
  ): Promise<void> {
    const { error } = await getSupabaseClient().rpc("soft_delete_document", {
      p_document_id: documentId,
      p_reason: reason,
    });
    if (error) fail("soft_delete_document", error);
  },

  /** A short-lived signed URL for a version's file (never a public URL). */
  async getOperationDocumentUrl(
    version: Pick<DocumentVersion, "id" | "storage_path">,
  ): Promise<string> {
    const { data, error } = await getSupabaseClient()
      .storage.from(OPERATION_DOCUMENTS_BUCKET)
      .createSignedUrl(
        version.storage_path,
        OPERATION_DOCUMENT_SIGNED_URL_EXPIRY_SECONDS,
      );
    if (error || !data) {
      console.error("getOperationDocumentUrl.error", error);
      throw new Error("No se pudo generar el enlace de descarga");
    }
    return data.signedUrl;
  },

  /** The operation's audit trail, oldest first (empty for roles RLS does not let read it). */
  async getOperationAuditLog(operationId: string): Promise<AuditLogEntry[]> {
    const { data, error } = await getSupabaseClient()
      .from("audit_log")
      .select("*")
      .eq("operation_id", operationId)
      .order("occurred_at", { ascending: true })
      .order("id", { ascending: true });
    if (error) fail("getOperationAuditLog", error);
    return (data ?? []) as AuditLogEntry[];
  },
});
